import React, { useCallback, useEffect, useState } from "react";
import UIkit from "uikit";

import StorageService from "../../../../services/storage.service";
import { MOVEMENT_TYPE_OPTIONS, formatCurrency, formatDateTime, labelFor } from "../data";

const today = new Date();
const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
const formatDate = (date) => date.toISOString().slice(0, 10);

const SectionRow = ({ title, children }) => (
  <div className="uk-margin-large-bottom">
    <div className="uk-flex uk-flex-between uk-flex-middle">
      <h2 className="uk-margin-remove">{title}</h2>
    </div>
    {children}
  </div>
);

const MetricCard = ({ label, icon, value }) => (
  <div>
    <div className="rbr-card uk-margin-small-right">
      <div className="rbr-card-head">
        <div className="rbr-card-title">
          <span className="rbr-ico" uk-icon={icon}></span>
          {label}
        </div>
      </div>
      <div className="hms-blue-number">{value}</div>
    </div>
  </div>
);

export default function InventoryLossReport() {
  const [criteria, setCriteria] = useState({
    start_date: formatDate(monthStart),
    end_date: formatDate(today),
  });
  const [summary, setSummary] = useState({});
  const [rows, setRows] = useState([]);

  const loadReport = useCallback(async () => {
    try {
      const resp = await StorageService.inventoryLossReport(criteria);
      setSummary(resp.data?.summary || {});
      setRows(resp.data?.rows || []);
    } catch (err) {
      UIkit.notification("Failed to load inventory loss report.", "error");
    }
  }, [criteria]);

  useEffect(() => {
    document.title = "Inventory Loss Report - RueBaRue";
    loadReport();
  }, [loadReport]);

  const handleChange = (key, value) => setCriteria((prev) => ({ ...prev, [key]: value }));

  return (
    <section id="dashboard-template">
      <div id="hms-page-title" className="uk-flex uk-flex-between uk-flex-middle">
        <h1 className="uk-heading-small uk-margin-remove">Inventory Loss Report</h1>
      </div>

      <div id="hms-main-body">
        <SectionRow title="Loss Summary">
          <div className="uk-grid-small uk-child-width-1-2 uk-child-width-1-3@m uk-margin-small-top" uk-grid="true">
            <MetricCard label="Lost Inventory Value" icon="minus-circle" value={formatCurrency(summary.lost_inventory_value)} />
            <MetricCard label="Damaged Inventory Value" icon="bolt" value={formatCurrency(summary.damaged_inventory_value)} />
            <MetricCard label="Disposed Inventory Value" icon="trash" value={formatCurrency(summary.disposed_inventory_value)} />
          </div>
        </SectionRow>

        <SectionRow title="Loss Movements">
          <div className="uk-card uk-card-small uk-card-default hms-form-card card-without-filters uk-margin-small-top">
            <div className="uk-overflow-auto">
              <form className="uk-form uk-flex uk-flex-middle hms-form guest-form-top-filters" onSubmit={(event) => event.preventDefault()}>
                <div className="uk-margin-small-right uk-margin-small-bottom">
                  <label className="uk-form-label">Start Date</label>
                  <input className="uk-input" type="date" value={criteria.start_date} onChange={(event) => handleChange("start_date", event.target.value)} />
                </div>
                <div className="uk-margin-small-right uk-margin-small-bottom">
                  <label className="uk-form-label">End Date</label>
                  <input className="uk-input" type="date" value={criteria.end_date} onChange={(event) => handleChange("end_date", event.target.value)} />
                </div>
              </form>

              <table className="uk-table uk-table-hover uk-table-divider hms-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th className="uk-table-expand">Item</th>
                    <th>Movement Type</th>
                    <th>Quantity</th>
                    <th>Unit Cost</th>
                    <th>Total Loss</th>
                    <th>Location</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr><td colSpan={8}><div className="uk-text-center uk-padding-small uk-text-meta">No loss movements found for this date range.</div></td></tr>
                  )}
                  {rows.map((row) => (
                    <tr key={row.id}>
                      <td>{formatDateTime(row.recorded_at)}</td>
                      <td>{row.item_name}<div className="uk-text-meta">{row.sku || "No SKU"}</div></td>
                      <td>{labelFor(MOVEMENT_TYPE_OPTIONS, row.movement_type)}</td>
                      <td>{row.quantity}</td>
                      <td>{formatCurrency(row.unit_cost)}</td>
                      <td>{formatCurrency(row.total_loss)}</td>
                      <td>{row.location_name || "-"}</td>
                      <td>{row.note || row.reason || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </SectionRow>
      </div>
    </section>
  );
}
